import { useCallback, useMemo, useState } from 'react'
import { Button } from '@/atoms/Button'
import { LoadingSpinnerIcon } from '@/atoms/Icons/LoadingSpinnerIcon'
import { TitleMD } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { Select } from '../../components/Inputs'
import { Page } from '../../components/Page'
import { useTorchApplication } from '../../components/TorchApplicationContext'
import { AssociatedProjectModal } from './AssociatedProjectModal'
import { useUpdateTorchApplicationProject } from './hooks/useUpdateTorchApplicationProject'
import { useUserGuildProjectQuery } from './hooks/useUserGuildProjectQuery'

export const AssociatedProject = () => {
  const { setPage, id: applicationId, applicationProjectId, setApplicationProjectId } = useTorchApplication()
  const { t } = useTranslate('common')
  const [showModal, setShowModal] = useState(false)
  const [updateTorchApplicationProject] = useUpdateTorchApplicationProject()

  const { loading, data } = useUserGuildProjectQuery()

  const selectOptions = useMemo(
    () => [
      {
        value: '',
        display: 'No, this is a new project',
      },
      ...(data?.guildUserProjects
        ?.filter((project) => project?.id && project?.name)
        .map((project) => ({
          value: project?.id as string,
          display: project?.name as string,
        })) || []),
    ],
    [data?.guildUserProjects],
  )

  const handleSelectChange = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const value = event.currentTarget.value

      setApplicationProjectId(value)

      if (applicationId) {
        updateTorchApplicationProject({
          variables: {
            input: {
              id: applicationId,
              projectId: value ? value : null,
            },
          },
        })
      }
    },
    [applicationId, setApplicationProjectId, updateTorchApplicationProject],
  )

  const closeModal = useCallback(() => setShowModal(false), [])
  const onSelectedProjectId = useCallback(
    (projectId: string) => setApplicationProjectId(projectId),
    [setApplicationProjectId],
  )

  return (
    <Page onBack={() => setPage(4)} onNext={() => setPage(6)}>
      {loading ? (
        <LoadingSpinnerIcon className="h-48 w-full py-8" color="black" size={72} />
      ) : (
        <>
          <div className="flex w-full flex-col justify-center gap-2">
            <TitleMD weight="bold" className="mb-8">
              {t(
                'shouldThisSubmissionBeLinkedToAnotherProject',
                'Should this submission be linked to another project?',
              )}
            </TitleMD>
            <Select
              label={t('availableProjects', 'Available Projects')}
              value={applicationProjectId || 'empty'}
              disabled={loading}
              name="project"
              onChange={handleSelectChange}
              options={selectOptions}
            />
            <AssociatedProjectModal
              applicationId={applicationId}
              closeModal={closeModal}
              showModal={showModal}
              onSelectedProjectId={onSelectedProjectId}
            />
          </div>
          <Button variant="oxide" onClick={() => setPage(6)} className="w-72">
            {t('continue', 'Continue')}
          </Button>
        </>
      )}
    </Page>
  )
}
